import { Component, OnInit } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AuthService } from 'src/app/shared/services/service';
import { apiUrl } from 'src/app/shared/services/api-url';
import { DashboardModel } from './dashboard.model';
import { Router } from '@angular/router';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import { resolveLanguage } from 'src/app/shared/language/_languages';
import { formatCorrectPercent } from './correct-percent';
import { SortMode } from './weakness';
import {
  BarSegments, ChapterRow, MIN_ANSWERED_FOR_RATING, PaperKey, PaperSummary, TrafficLight,
  barSegments, chapterNumbersFor, sortByWeaknessRated, splitByPaper, summarize, trafficLight, weakestRated
} from './paper-overview';

/** Everything the template needs for one paper tab. */
export interface PaperView {
  key: PaperKey;
  labelKey: string;
  subtitleKey: string;
  rows: DashboardModel[];
  summary: PaperSummary;
  bar: BarSegments;
  weakest: DashboardModel[];
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

  isVisibleModal = false;
  isSpinner = false;
  capitalTableList: DashboardModel[];
  total: DashboardModel;
  papers: PaperView[] = [];
  activePaper: PaperKey = 'A';
  readonly minAnswered = MIN_ANSWERED_FOR_RATING;
  private originalList: DashboardModel[] = [];
  /** Capital id -> displayed chapter number (see chapterNumber). */
  private chapterNumbers = new Map<number, number>();
  /** 'default' keeps the order delivered by the API; 'weakness' sorts rated chapters by % correct ascending. */
  sortMode: SortMode = 'default';

  constructor(private modal: NzModalService, private apiService: AuthService, private router:Router, 
              private notification: NotificationService, private translate: TranslatePipe) { 
    this.capitalTableList = [];
    this.total = new DashboardModel();
    const savedPaper = sessionStorage.getItem('dashboardPaper');
    if (savedPaper === 'A' || savedPaper === 'B') {
      this.activePaper = savedPaper;
    }
  }

  async ngOnInit(): Promise<void> {
    await this.getUserInfo();
    this.loadDashboardTableList();
  }

  resetallQuestions(): void {
    this.getUserInfo();
    this.apiService.ServerGet(apiUrl.resetQuestions).subscribe((res => {
      this.notification.sendNotification(this.translate.transform('resetQuestionNotification'), 'success');
      this.loadDashboardTableList();
    }),err => {
      if(err.status == 404){
        this.notification.sendNotification(this.translate.transform('resetNoQuestionNotification'), 'error');
      }
    });
  }

  openModel(): void {
    this.modal.warning({
        nzTitle: this.translate.transform('resetConfirmMessage'),
        nzOkText: this.translate.transform('yes'),
        nzOkType: 'primary',
        nzOnOk: () => this.resetallQuestions(),
        nzCancelText: this.translate.transform('no'),
    });
  }

  cancel(): void {
    this.isVisibleModal = false;
  }

  loadDashboardTableList(): void {
    this.isSpinner = true;
    this.apiService.ServerGet(apiUrl.dashboard).subscribe((res: any) => {
      this.capitalTableList = res.capitals;
      this.originalList = res.capitals.slice();
      this.applySort();

      this.total.total_question = res.total_question;
      this.total.blank_question_count = res.total_blank_question;
      this.total.correct_question_count = res.total_correct_question;
      this.total.wrong_question_count = res.total_wrong_question;
      this.isSpinner = false;
    })
  }

  practiceWrong(): void {
    this.getUserInfo();

    if(this.total.wrong_question_count > 0){
      this.router.navigate(['/question', 1], {queryParams: {status: 'wrong' }});
    }
    else {
      this.notification.sendNotification(this.translate.transform('wrongQuestionNotification'), 'info');
    }
  }

  practiceBlank(): void{
    this.getUserInfo();
    if(this.total.blank_question_count > 0){
      this.router.navigate(['/question', 1], {queryParams: {status: 'blank' }});
    }
    else {
      this.notification.sendNotification(this.translate.transform('unansweredQuestionNotification'), 'info');
    }
  }

  setSort(mode: SortMode): void {
    this.sortMode = mode;
    this.applySort();
  }

  /** Rebuilds both paper tabs from the API list. Chapters are grouped by Capital id (see paper-overview.ts). */
  applySort(): void {
    this.capitalTableList = this.originalList.slice();
    const groups = splitByPaper(this.originalList);
    this.chapterNumbers = chapterNumbersFor(groups.A, groups.B);
    const view = (key: PaperKey, labelKey: string, subtitleKey: string): PaperView => {
      const inApiOrder = groups[key];
      const summary = summarize(inApiOrder as ChapterRow[]);
      return {
        key,
        labelKey,
        subtitleKey,
        rows: this.sortMode === 'weakness' ? sortByWeaknessRated(inApiOrder) : inApiOrder,
        summary,
        bar: barSegments(summary.correct, summary.wrong, summary.total),
        weakest: weakestRated(inApiOrder, 3)
      };
    };
    this.papers = [
      view('A', 'mockPaperA', 'ovPaperASubtitle'),
      view('B', 'mockPaperB', 'ovPaperBSubtitle')
    ];
  }

  selectPaper(key: PaperKey): void {
    this.activePaper = key;
    sessionStorage.setItem('dashboardPaper', key);
  }

  /** Arrow keys, Home and End move between the two tabs (WAI-ARIA tabs pattern). */
  onTabKeydown(event: KeyboardEvent): void {
    const order: PaperKey[] = ['A', 'B'];
    let next: PaperKey | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = order[(order.indexOf(this.activePaper) + 1) % order.length];
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = order[(order.indexOf(this.activePaper) + order.length - 1) % order.length];
    } else if (event.key === 'Home') {
      next = 'A';
    } else if (event.key === 'End') {
      next = 'B';
    }
    if (next === null) {
      return;
    }
    event.preventDefault();
    this.selectPaper(next);
    const target = document.getElementById('paper-tab-' + next);
    if (target) {
      target.focus();
    }
  }

  /**
   * Displayed chapter number: fixed, consecutive in the default order after grouping
   * (Paper A = 1..9, Paper B = 10..15). It does not change with the sort mode and is not the API position.
   */
  chapterNumber(row: DashboardModel): number {
    return this.chapterNumbers.get(row.id) || 0;
  }

  /** Position of the chapter in the API list (same index as the chapter list); used for the stored chapter index. */
  private apiPosition(row: DashboardModel): number {
    return this.originalList.indexOf(row) + 1;
  }

  light(row: DashboardModel): TrafficLight {
    return trafficLight(row);
  }

  answered(row: DashboardModel): number {
    return row.correct_question_count + row.wrong_question_count;
  }

  segments(row: DashboardModel): BarSegments {
    return barSegments(row.correct_question_count, row.wrong_question_count, row.total_question);
  }

  lightKey(light: TrafficLight): string {
    return { green: 'ovStatusGood', yellow: 'ovStatusFair', red: 'ovStatusWeak', none: 'ovStatusNoData' }[light];
  }

  lightSymbol(light: TrafficLight): string {
    return { green: '\u25B2', yellow: '\u25A0', red: '\u25BC', none: '\u25CB' }[light];
  }

  /** Translates a key and fills `{name}` placeholders. */
  fmt(key: string, params: { [name: string]: string | number }): string {
    let text = String(this.translate.transform(key));
    Object.keys(params).forEach(name => {
      text = text.split('{' + name + '}').join(String(params[name]));
    });
    return text;
  }

  barLabel(correct: number, wrong: number, open: number): string {
    return this.fmt('ovBarLabel', { correct, wrong, open });
  }

  /** Opens the question list of one chapter (same route as the chapter list). */
  practiceChapter(row: DashboardModel): void {
    sessionStorage.setItem('currentCapitalIndex', String(this.apiPosition(row)));
    this.router.navigate(['/questions'], { queryParams: { capital: row.id } });
  }

  trackByPaper(_index: number, paper: PaperView): string {
    return paper.key;
  }

  trackById(_index: number, row: DashboardModel): number {
    return row.id;
  }

  get activePaperView(): PaperView | undefined {
    return this.papers.find(p => p.key === this.activePaper);
  }

  get currentLang(): string {
    return resolveLanguage(localStorage.getItem('language'));
  }

  percentCorrect(row: Pick<DashboardModel, 'correct_question_count' | 'wrong_question_count'>): string {
    return formatCorrectPercent(row.correct_question_count, row.wrong_question_count);
  }

  async getUserInfo(): Promise<void> {
    await this.apiService.ServerGet(apiUrl.user).toPromise().then(((res:any) => {
      localStorage.setItem('language', res.language);
    }))
  }
}
